import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
	User,
	Phone,
	Mail,
	Calendar,
	IdCard,
	Fingerprint,
	Check,
	X,
	Save,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { SubpageHeader } from '@/components/ui/SubpageHeader';
import { getUserData } from '@/utils/cookies';
import { getImageUrl } from '@/utils/imageUrl';
import { useGetUser, usePatchUser } from '@/services';
import { maskCPF, maskPhone, maskDate } from '@/utils';

const UserProfile = () => {
	const navigate = useNavigate();
	const cookieUser = getUserData();
	const { data: userFromApi, isLoading } = useGetUser(
		{ isMe: true },
		{ refetchOnWindowFocus: false },
	);
	const { mutateAsync } = usePatchUser();

	const [currentUser, setCurrentUser] = React.useState(
		userFromApi ?? cookieUser,
	);
	const [photoFailed, setPhotoFailed] = React.useState(false);

	const resolvedUser = currentUser ?? userFromApi ?? cookieUser;

	useEffect(() => {
		if (userFromApi) {
			setCurrentUser(userFromApi);
		}
	}, [userFromApi]);

	useEffect(() => {
		setPhotoFailed(false);
	}, [resolvedUser?.profile_photo_url]);

	useEffect(() => {
		if (!isLoading && !userFromApi && !cookieUser) {
			navigate('/login');
		}
	}, [cookieUser, isLoading, navigate, userFromApi]);

	const formDefaults = useMemo(() => {
		const getDisplayBirthday = () => {
			const raw = resolvedUser?.birthday || '';
			if (raw.includes('T')) {
				const datePart = raw.split('T')[0];
				const [year, month, day] = datePart.split('-');
				return `${day}/${month}/${year}`;
			}
			return raw;
		};
		return {
			first_name: resolvedUser?.first_name || '',
			last_name: resolvedUser?.last_name || '',
			phone_number: maskPhone(resolvedUser?.phone_number || ''),
			email: resolvedUser?.email || '',
			birthday: maskDate(getDisplayBirthday()),
			cpf: maskCPF(resolvedUser?.cpf || ''),
		};
	}, [
		resolvedUser?.id,
		resolvedUser?.first_name,
		resolvedUser?.last_name,
		resolvedUser?.phone_number,
		resolvedUser?.email,
		resolvedUser?.birthday,
		resolvedUser?.cpf,
	]);

	const {
		register,
		handleSubmit,
		setValue,
		reset,
		formState: { isDirty, dirtyFields },
	} = useForm({
		defaultValues: formDefaults,
	});

	if (!resolvedUser && isLoading) {
		return null;
	}

	if (!resolvedUser) {
		return null;
	}

	const birthdayToIso = (raw: string): string | undefined => {
		const s = raw.trim();
		if (!s) return undefined;
		if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
			const [day, month, year] = s.split('/');
			return new Date(
				`${year}-${month}-${day}T00:00:00.000Z`,
			).toISOString();
		}
		if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
			return new Date(`${s}T00:00:00.000Z`).toISOString();
		}
		if (s.includes('T')) {
			return new Date(s).toISOString();
		}
		const digits = s.replace(/\D/g, '');
		if (digits.length === 8) {
			const day = digits.slice(0, 2);
			const month = digits.slice(2, 4);
			const year = digits.slice(4, 8);
			return new Date(
				`${year}-${month}-${day}T00:00:00.000Z`,
			).toISOString();
		}
		return undefined;
	};

	const onSubmit = async (data: typeof formDefaults) => {
		const payload: Partial<Users.Update> = {};

		const nameChanged =
			data.first_name !== formDefaults.first_name ||
			!!dirtyFields.first_name;
		const lastChanged =
			data.last_name !== formDefaults.last_name ||
			!!dirtyFields.last_name;

		const phoneDigits = (data.phone_number || '').replace(/\D/g, '');
		const defaultPhoneDigits = (formDefaults.phone_number || '').replace(
			/\D/g,
			'',
		);
		const phoneChanged =
			phoneDigits !== defaultPhoneDigits || !!dirtyFields.phone_number;

		const birthdayDigits = (data.birthday || '').replace(/\D/g, '');
		const defaultBirthdayDigits = (formDefaults.birthday || '').replace(
			/\D/g,
			'',
		);
		const birthdayChanged =
			birthdayDigits !== defaultBirthdayDigits || !!dirtyFields.birthday;

		if (nameChanged) {
			payload.first_name = data.first_name;
		}
		if (lastChanged) {
			payload.last_name = data.last_name;
		}
		if (phoneChanged) {
			payload.phone_number = phoneDigits;
		}

		if (birthdayChanged) {
			const rawBirthday = (data.birthday || '').trim();
			const iso = birthdayToIso(rawBirthday);
			if (iso) {
				payload.birthday = iso;
			}
		}

		if (Object.keys(payload).length === 0) {
			return;
		}

		try {
			const userUpdated = await mutateAsync({
				id: resolvedUser.id,
				...payload,
			});

			setCurrentUser(userUpdated);
			reset({
				first_name: userUpdated.first_name || '',
				last_name: userUpdated.last_name || '',
				phone_number: maskPhone(userUpdated.phone_number || ''),
				email: userUpdated.email || '',
				birthday: maskDate(
					userUpdated.birthday
						? userUpdated.birthday.includes('T')
							? (() => {
									const [year, month, day] =
										userUpdated.birthday
											.split('T')[0]
											.split('-');
									return `${day}/${month}/${year}`;
							  })()
							: userUpdated.birthday
						: '',
				),
				cpf: maskCPF(userUpdated.cpf || ''),
			});
		} catch {
			// Erro já notificado pelo onError de usePatchUser
		}
	};

	const initials = `${resolvedUser.first_name?.charAt(0) || ''}${
		resolvedUser.last_name?.charAt(0) || ''
	}`.toUpperCase();

	const hasGooglePermissions = !!resolvedUser.google_permissions_allowed;
	const hasVerifiedEmail = !!resolvedUser.verified_email;
	const profilePhotoUrl = resolvedUser.profile_photo_url?.trim()
		? getImageUrl(resolvedUser.profile_photo_url)
		: null;
	const showProfilePhoto = Boolean(profilePhotoUrl) && !photoFailed;

	const onBack = () => navigate('/home');

	return (
		<div
			data-site-tutorial="profile-root"
			className="flex min-h-screen w-full items-start justify-center bg-[#f8f9fa] px-3 py-4 md:px-4 md:py-5"
		>
			<div className="animate-in fade-in w-full max-w-[1280px] space-y-6 duration-500">
				<SubpageHeader title="Meu Perfil" onBack={onBack} />

				{/* CARD DE IDENTIDADE */}
				<div className="flex items-center gap-6 rounded-[2rem] border border-stone-100 bg-white p-6 shadow-sm">
					<div className="relative shrink-0">
						{showProfilePhoto && profilePhotoUrl ? (
							<img
								src={profilePhotoUrl}
								alt={`${resolvedUser.first_name} ${resolvedUser.last_name}`}
								className="h-20 w-20 rounded-3xl object-cover shadow-xl shadow-brand-orange/15"
								referrerPolicy="no-referrer"
								onError={() => setPhotoFailed(true)}
							/>
						) : (
							<div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-brand-orange text-2xl font-black text-white shadow-xl shadow-brand-orange/25">
								{initials}
							</div>
						)}
					</div>

					<div className="space-y-1.5 text-left">
						<div className="flex items-center gap-2">
							<h2 className="text-xl font-black text-stone-900">
								{resolvedUser.first_name}{' '}
								{resolvedUser.last_name}
							</h2>
							<div
								className={`h-2.5 w-2.5 rounded-full border-2 border-white shadow-sm ${
									hasVerifiedEmail
										? 'bg-brand-orange'
										: 'bg-stone-300'
								}`}
							/>
						</div>
						<div className="flex flex-wrap gap-2 pt-1">
							<span
								className={`flex items-center gap-1 rounded-md border px-2 py-1 text-[8px] font-black uppercase tracking-widest ${
									hasGooglePermissions
										? 'border-brand-green/25 bg-brand-green/10 text-brand-green'
										: 'border-stone-200 bg-stone-50 text-stone-500'
								}`}
							>
								{hasGooglePermissions ? (
									<Check size={8} strokeWidth={4} />
								) : (
									<X
										size={8}
										strokeWidth={4}
										className="opacity-60"
									/>
								)}
								{hasGooglePermissions
									? 'Permissões concedidas no Google'
									: 'Permissões não concedidas no Google'}
							</span>

							<span
								className={`flex items-center gap-1 rounded-md border px-2 py-1 text-[8px] font-black uppercase tracking-widest ${
									hasVerifiedEmail
										? 'border-brand-green/25 bg-brand-green/10 text-brand-green'
										: 'border-stone-200 bg-stone-50 text-stone-500'
								}`}
							>
								{hasVerifiedEmail ? (
									<Check size={8} strokeWidth={4} />
								) : (
									<X
										size={8}
										strokeWidth={4}
										className="opacity-60"
									/>
								)}
								{hasVerifiedEmail
									? 'Verificado pelo Google'
									: 'Não verificado pelo Google'}
							</span>
						</div>
					</div>
				</div>

				{/* SEÇÃO DE FORMULÁRIO */}
				<div className="space-y-6 rounded-[2rem] border border-stone-100 bg-white p-6 shadow-sm md:p-8">
					<div className="flex items-center gap-3">
						<div className="rounded-lg bg-brand-orange/10 p-2 text-brand-orange">
							<Fingerprint size={20} />
						</div>
						<h3 className="text-lg font-black tracking-tight text-stone-900">
							Informações Pessoais
						</h3>
					</div>

					<form
						onSubmit={handleSubmit(onSubmit)}
						className="space-y-6"
					>
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<div className="space-y-1.5 text-left">
								<label className="ml-1 flex items-center gap-2 text-[9px] font-black uppercase leading-none tracking-widest text-brand-orange">
									<User size={10} /> Nome
								</label>
								<input
									{...register('first_name')}
									className="w-full rounded-xl border border-stone-100 bg-stone-50/50 px-4 py-2.5 text-sm font-bold text-stone-700 outline-none transition-all focus:border-brand-orange/40 focus:bg-white focus:ring-1 focus:ring-brand-orange/20"
								/>
							</div>
							<div className="space-y-1.5 text-left">
								<label className="ml-1 flex items-center gap-2 text-[9px] font-black uppercase leading-none tracking-widest text-brand-orange">
									<User size={10} /> Sobrenome
								</label>
								<input
									{...register('last_name')}
									className="w-full rounded-xl border border-stone-100 bg-stone-50/50 px-4 py-2.5 text-sm font-bold text-stone-700 outline-none transition-all focus:border-brand-orange/40 focus:bg-white focus:ring-1 focus:ring-brand-orange/20"
								/>
							</div>
						</div>

						<div className="space-y-1.5 text-left">
							<label className="ml-1 flex items-center gap-2 text-[9px] font-black uppercase leading-none tracking-widest text-brand-orange">
								<Phone size={10} /> Telefone para contato
							</label>
							<input
								{...register('phone_number')}
								onChange={(e) =>
									setValue(
										'phone_number',
										maskPhone(e.target.value),
										{
											shouldDirty: true,
										},
									)
								}
								className="w-full rounded-xl border border-stone-100 bg-stone-50/50 px-4 py-2.5 text-sm font-bold text-stone-700 outline-none transition-all focus:border-brand-orange/40 focus:bg-white focus:ring-1 focus:ring-brand-orange/20"
							/>
						</div>

						<div className="space-y-1.5 text-left">
							<label className="ml-1 flex items-center gap-2 text-[9px] font-black uppercase leading-none tracking-widest text-brand-orange">
								<Mail size={10} /> E-mail
							</label>
							<input
								{...register('email')}
								readOnly
								className="w-full cursor-not-allowed rounded-xl border border-dashed border-stone-200 bg-stone-50/30 px-4 py-2.5 text-sm font-bold text-stone-400 outline-none"
							/>
						</div>

						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<div className="space-y-1.5 text-left">
								<label className="ml-1 flex items-center gap-2 text-[9px] font-black uppercase leading-none tracking-widest text-brand-orange">
									<Calendar size={10} /> Nascimento
								</label>
								<input
									{...register('birthday')}
									onChange={(e) =>
										setValue(
											'birthday',
											maskDate(e.target.value),
											{
												shouldDirty: true,
											},
										)
									}
									className="w-full rounded-xl border border-stone-100 bg-stone-50/50 px-4 py-2.5 text-sm font-bold text-stone-700 outline-none transition-all focus:border-brand-orange/40 focus:bg-white focus:ring-1 focus:ring-brand-orange/20"
								/>
							</div>
							<div className="space-y-1.5 text-left">
								<label className="ml-1 flex items-center gap-2 text-[9px] font-black uppercase leading-none tracking-widest text-brand-orange">
									<IdCard size={10} /> CPF
								</label>
								<input
									{...register('cpf')}
									readOnly
									className="w-full cursor-not-allowed rounded-xl border border-dashed border-stone-200 bg-stone-50/30 px-4 py-2.5 text-sm font-bold text-stone-400 outline-none"
								/>
							</div>
						</div>

						{/* EXIBIÇÃO CONDICIONAL DO BOTÃO: Aparece apenas se houver alteração (isDirty) */}
						{isDirty && (
							<div className="animate-in fade-in slide-in-from-bottom-2 flex justify-end pt-2 duration-300">
								<Button
									type="submit"
									text="Salvar Alterações"
									className="hover:bg-brand-orange/92 rounded-xl border-2 border-brand-orange bg-brand-orange px-8 py-3 text-xs font-black uppercase tracking-[0.12em] text-white shadow-sm transition-all duration-150 hover:border-brand-orange active:scale-[0.98]"
									endIcon={
										<Save
											size={16}
											className="ml-1 shrink-0"
										/>
									}
								/>
							</div>
						)}
					</form>
				</div>
			</div>
		</div>
	);
};

export { UserProfile };
